<template>
  <div class="nx-list-page contract-settlement-page">
    <avue-crud
      ref="crud"
      v-model="form"
      :option="option"
      :table-loading="loading"
      :data="data"
      :page.sync="page"
      :search.sync="query"
      :defaults.sync="defaults"
      @selection-change="selectionChange"
      @search-change="searchChange"
      @search-reset="searchReset"
      @refresh-change="refreshChange"
      @current-change="currentChange"
      @size-change="sizeChange"
      @on-load="onLoad"
    >
      <template slot="menuLeft">
        <el-button size="small" icon="el-icon-plus" @click="openGenerateDialog">补生成结算单</el-button>
        <el-button size="small" icon="el-icon-s-operation" :loading="batchGenerateLoading" @click="openBatchGenerateDialog">批量生成</el-button>
        <el-button size="small" icon="el-icon-check" :loading="batchSubmitLoading" @click="handleBatchSubmit">批量提交确认</el-button>
        <el-button size="small" icon="el-icon-download" :loading="exportLoading" @click="handleExport">导出</el-button>
      </template>

      <template slot="billStatus" slot-scope="{ row }">
        <matrix-tag :color="statusColor(row.billStatus)" :text="statusLabel(row.billStatus)" />
      </template>
      <template slot="amountWithoutTax" slot-scope="{ row }">
        <span class="money">{{ money(row.amountWithoutTax) }}</span>
      </template>
      <template slot="taxAmount" slot-scope="{ row }">
        <span class="money">{{ money(row.taxAmount) }}</span>
      </template>
      <template slot="amountWithTax" slot-scope="{ row }">
        <span class="money strong">{{ money(row.amountWithTax) }}</span>
      </template>
      <template slot="settlementMonthValue" slot-scope="{ row }">
        {{ settlementMonthLabel(row) }}
      </template>
      <template slot="menu" slot-scope="{ row }">
        <el-button type="text" size="small" @click="openDetail(row)">查看</el-button>
        <el-button v-if="canEdit(row)" type="text" size="small" @click="openDetail(row, 'items')">编辑调整</el-button>
        <el-button v-if="canEdit(row)" type="text" size="small" @click="handleSubmit(row)">提交确认</el-button>
        <el-button v-if="canEdit(row)" type="text" size="small" class="danger-link" @click="handleCancel(row)">作废</el-button>
      </template>
    </avue-crud>

    <el-drawer
      title="合同结算单详情"
      :visible.sync="detailVisible"
      direction="rtl"
      size="82%"
      append-to-body
      @closed="resetDetail"
    >
      <template v-if="detail">
        <div class="detail-head">
          <div v-for="item in detailItems" :key="item.label" class="detail-card">
            <div class="detail-label">{{ item.label }}</div>
            <div class="detail-value">{{ item.value }}</div>
          </div>
        </div>
        <div class="summary">
          <div class="summary-item"><span>系统不含税</span><b>{{ money(detail.systemAmountWithoutTax) }}</b></div>
          <div class="summary-item"><span>调整不含税</span><b>{{ money(detail.adjustAmountWithoutTax) }}</b></div>
          <div class="summary-item"><span>手工填报不含税</span><b>{{ money(detail.manualAmountWithoutTax) }}</b></div>
          <div class="summary-item"><span>税额合计</span><b>{{ money(detail.taxAmount) }}</b></div>
          <div class="summary-item"><span>价税合计</span><b>{{ money(detail.amountWithTax) }}</b></div>
        </div>

        <el-tabs v-model="activeTab">
          <el-tab-pane label="结算明细" name="items">
            <el-table :data="detail.items || []" border size="small" empty-text="暂无结算明细">
              <el-table-column label="结算周期" min-width="160">
                <template slot-scope="{ row }">{{ row.cycleStartDate }} 至 {{ row.cycleEndDate }}</template>
              </el-table-column>
              <el-table-column prop="feeItemName" label="费目" min-width="130" show-overflow-tooltip />
              <el-table-column prop="varName" label="品名/煤种" min-width="110" show-overflow-tooltip />
              <el-table-column prop="measureUnit" label="单位" width="80" />
              <el-table-column prop="unitPriceWithoutTax" label="不含税单价" width="110" align="right" />
              <el-table-column label="结算数量" width="110" align="right">
                <template slot-scope="{ row }">{{ quantityByUnit(row, 'system') }}</template>
              </el-table-column>
              <el-table-column label="调整数量" width="110" align="right">
                <template slot-scope="{ row }">{{ quantityByUnit(row, 'adjust') }}</template>
              </el-table-column>
              <el-table-column prop="finalQuantity" label="最终结算量" width="110" align="right" />
              <el-table-column label="税率" width="80" align="right">
                <template slot-scope="{ row }">{{ money(row.taxRate, 4) }}%</template>
              </el-table-column>
              <el-table-column label="不含税金额" width="120" align="right">
                <template slot-scope="{ row }">{{ money(row.amountWithoutTax) }}</template>
              </el-table-column>
              <el-table-column label="税额" width="110" align="right">
                <template slot-scope="{ row }">{{ money(row.taxAmount) }}</template>
              </el-table-column>
              <el-table-column label="价税合计" width="120" align="right">
                <template slot-scope="{ row }">{{ money(row.amountWithTax) }}</template>
              </el-table-column>
              <el-table-column label="操作" width="160" fixed="right">
                <template slot-scope="{ row }">
                  <el-button v-if="canEdit(detail) && row.settlementType === 'BUSINESS'" type="text" size="small" @click="openAdjustDialog(row)">调整</el-button>
                  <el-button v-if="canEdit(detail) && row.settlementType === 'MANUAL'" type="text" size="small" @click="openManualDialog(row)">填报</el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
          <el-tab-pane label="操作记录" name="logs">
            <el-timeline>
              <el-timeline-item v-for="log in detail.logs || []" :key="log.id" :timestamp="log.operationTime" placement="top">
                <b>{{ operationLabel(log.operationType) }}</b>
                <span class="sub-line log-operator">{{ log.operatorName || '-' }}</span>
                <div>{{ log.operationRemark || '-' }}</div>
              </el-timeline-item>
            </el-timeline>
          </el-tab-pane>
        </el-tabs>
      </template>
    </el-drawer>

    <el-dialog title="补生成结算单" :visible.sync="generateVisible" width="640px" append-to-body>
      <el-form ref="generateForm" :model="generateForm" :rules="generateRules" label-width="110px">
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="结算年月" prop="settlementMonthValue">
              <el-date-picker v-model="generateForm.settlementMonthValue" type="month" format="yyyy-MM" value-format="yyyy-MM" placeholder="请选择" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12"><el-form-item label="合同ID" prop="contractId"><el-input v-model="generateForm.contractId" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="合同编号" prop="contractNo"><el-input v-model="generateForm.contractNo" /></el-form-item></el-col>
          <el-col :span="24"><el-form-item label="合同名称" prop="contractName"><el-input v-model="generateForm.contractName" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="客户编码"><el-input v-model="generateForm.custNo" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="客户名称"><el-input v-model="generateForm.custName" /></el-form-item></el-col>
          <el-col :span="24"><el-form-item label="补生成原因" prop="generateReason"><el-input v-model="generateForm.generateReason" type="textarea" :rows="3" /></el-form-item></el-col>
        </el-row>
      </el-form>
      <span slot="footer">
        <el-button @click="generateVisible = false">取消</el-button>
        <el-button type="primary" :loading="generateLoading" @click="handleGenerate">确定</el-button>
      </span>
    </el-dialog>

    <el-dialog title="批量生成结算单" :visible.sync="batchGenerateVisible" width="520px" append-to-body>
      <el-form ref="batchGenerateForm" :model="batchGenerateForm" :rules="batchGenerateRules" label-width="110px">
        <el-form-item label="结算年月" prop="settlementMonthValue">
          <el-date-picker v-model="batchGenerateForm.settlementMonthValue" type="month" format="yyyy-MM" value-format="yyyy-MM" placeholder="请选择" style="width: 100%" />
        </el-form-item>
        <el-form-item label="生成说明">
          <el-input v-model="batchGenerateForm.generateReason" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="batchGenerateVisible = false">取消</el-button>
        <el-button type="primary" :loading="batchGenerateLoading" @click="handleBatchGenerate">确定</el-button>
      </span>
    </el-dialog>

    <el-dialog title="业务量调整" :visible.sync="adjustVisible" width="520px" append-to-body>
      <el-form ref="adjustForm" :model="adjustForm" :rules="adjustRules" label-width="110px">
        <el-form-item label="费目"><el-input :value="currentItem && currentItem.feeItemName" disabled /></el-form-item>
        <el-form-item label="计量单位"><el-input :value="currentItem && currentItem.measureUnit" disabled /></el-form-item>
        <el-form-item label="调整数量">
          <el-input-number v-model="adjustForm.adjustQuantity" :disabled="!canAdjustQuantity(currentItem)" controls-position="right" style="width: 100%" />
        </el-form-item>
        <el-form-item label="调整原因" prop="adjustReason"><el-input v-model="adjustForm.adjustReason" type="textarea" :rows="3" /></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="adjustVisible = false">取消</el-button>
        <el-button type="primary" :loading="itemSaving" @click="handleAdjust">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog title="手工填报" :visible.sync="manualVisible" width="520px" append-to-body>
      <el-form ref="manualForm" :model="manualForm" :rules="manualRules" label-width="120px">
        <el-form-item label="费目"><el-input :value="currentItem && currentItem.feeItemName" disabled /></el-form-item>
        <el-form-item label="计量单位"><el-input :value="currentItem && currentItem.measureUnit" disabled /></el-form-item>
        <el-form-item label="税率"><el-input :value="currentItem ? money(currentItem.taxRate, 4) + '%' : ''" disabled /></el-form-item>
        <el-form-item label="填报数量" prop="finalQuantity"><el-input-number v-model="manualForm.finalQuantity" :min="0" :precision="4" controls-position="right" /></el-form-item>
        <el-form-item label="不含税单价" prop="unitPriceWithoutTax"><el-input-number v-model="manualForm.unitPriceWithoutTax" :min="0" :precision="6" controls-position="right" /></el-form-item>
        <el-form-item label="不含税金额"><el-input :value="manualAmount.amountWithoutTax" disabled /></el-form-item>
        <el-form-item label="税额"><el-input :value="manualAmount.taxAmount" disabled /></el-form-item>
        <el-form-item label="价税合计"><el-input :value="manualAmount.amountWithTax" disabled /></el-form-item>
        <el-form-item label="填报说明"><el-input v-model="manualForm.fillRemark" type="textarea" :rows="3" /></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="manualVisible = false">取消</el-button>
        <el-button type="primary" :loading="itemSaving" @click="handleManualFill">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import crudMixins from '@/mixins/crud-mixins';
import matrixTag from '@/components/matrix/matrix-tag';
import { downloadXls } from '@/util/util';
import {
  getList,
  getDetail,
  generateBill,
  batchGenerateBill,
  adjustItem,
  manualFillItem,
  submitBill,
  cancelBill,
  exportExcel,
} from './api';

const STATUS_OPTIONS = [
  { label: '待填报', value: 'WAIT_FILL' },
  { label: '待确认', value: 'WAIT_CONFIRM' },
  { label: '已完成', value: 'DONE' },
  { label: '已作废', value: 'CANCELLED' },
];

export default {
  name: 'finance-contract-settlement-bill',
  components: { matrixTag },
  mixins: [crudMixins],
  data() {
    return {
      apiConfig: { getList },
      exportLoading: false,
      batchSubmitLoading: false,
      detailVisible: false,
      detail: null,
      activeTab: 'items',
      generateVisible: false,
      generateLoading: false,
      batchGenerateVisible: false,
      batchGenerateLoading: false,
      generateForm: {
        settlementMonthValue: this.currentSettlementMonth(),
      },
      batchGenerateForm: {
        settlementMonthValue: this.currentSettlementMonth(),
        generateReason: '批量生成',
      },
      currentItem: null,
      adjustVisible: false,
      manualVisible: false,
      itemSaving: false,
      adjustForm: {},
      manualForm: {},
      generateRules: {
        settlementMonthValue: [{ required: true, message: '请选择结算年月', trigger: 'change' }],
        contractId: [{ required: true, message: '请输入合同ID', trigger: 'blur' }],
        contractNo: [{ required: true, message: '请输入合同编号', trigger: 'blur' }],
        contractName: [{ required: true, message: '请输入合同名称', trigger: 'blur' }],
        generateReason: [{ required: true, message: '请输入补生成原因', trigger: 'blur' }],
      },
      batchGenerateRules: {
        settlementMonthValue: [{ required: true, message: '请选择结算年月', trigger: 'change' }],
      },
      adjustRules: {
        adjustReason: [{ required: true, message: '请输入调整原因', trigger: 'blur' }],
      },
      manualRules: {
        finalQuantity: [{ required: true, message: '请输入填报数量', trigger: 'change' }],
        unitPriceWithoutTax: [{ required: true, message: '请输入不含税单价', trigger: 'change' }],
      },
      option: {
        ...this.$store.state.publicOption,
        addBtn: false,
        editBtn: false,
        delBtn: false,
        viewBtn: false,
        selection: true,
        menuWidth: 260,
        searchMenuSpan: 6,
        column: [
          { label: '结算单号', prop: 'settlementNo', search: true, minWidth: 150 },
          { label: '结算年月', prop: 'settlementMonthValue', type: 'month', search: true, slot: true, width: 110, format: 'yyyy-MM', valueFormat: 'yyyy-MM', searchValue: this.currentSettlementMonth() },
          { label: '合同编号', prop: 'contractNo', search: true, minWidth: 150 },
          { label: '合同名称', prop: 'contractName', search: true, minWidth: 210 },
          {
            label: '客户',
            prop: 'custNo',
            type: 'select',
            search: true,
            hide: true,
            display: false,
            dicUrl: '/api/produce/blade-dept/select/distinct/select',
            props: {
              label: 'deptName',
              value: 'id',
            },
            width: 150,
          },
          { label: '客户名称', prop: 'custName', minWidth: 150 },
          { label: '生成时间', prop: 'generateTime', type: 'datetime', format: 'yyyy-MM-dd HH:mm:ss', valueFormat: 'yyyy-MM-dd HH:mm:ss', width: 170 },
          { label: '不含税金额', prop: 'amountWithoutTax', slot: true, width: 120, align: 'right' },
          { label: '税额', prop: 'taxAmount', slot: true, width: 110, align: 'right' },
          { label: '价税合计', prop: 'amountWithTax', slot: true, width: 120, align: 'right' },
          { label: '状态', prop: 'billStatus', slot: true, type: 'select', search: true, width: 100, dicData: STATUS_OPTIONS },
        ],
      },
    };
  },
  computed: {
    detailItems() {
      const d = this.detail;
      if (!d) return [];
      return [
        { label: '结算单号', value: d.settlementNo || '-' },
        { label: '合同编号', value: d.contractNo || '-' },
        { label: '合同名称', value: d.contractName || '-' },
        { label: '客户名称', value: d.custName || '-' },
        { label: '生成时间', value: d.generateTime || '-' },
        { label: '状态', value: this.statusLabel(d.billStatus) },
        { label: '结算年月', value: `${d.settlementYear}-${this.padMonth(d.settlementMonth)}` },
      ];
    },
    manualAmount() {
      const quantity = Number(this.manualForm.finalQuantity || 0);
      const price = Number(this.manualForm.unitPriceWithoutTax || 0);
      const rate = Number(this.currentItem && this.currentItem.taxRate ? this.currentItem.taxRate : 0);
      const amountWithoutTax = quantity * price;
      const taxAmount = amountWithoutTax * rate / 100;
      const amountWithTax = amountWithoutTax + taxAmount;
      return {
        amountWithoutTax: this.money(amountWithoutTax),
        taxAmount: this.money(taxAmount),
        amountWithTax: this.money(amountWithTax),
      };
    },
  },
  methods: {
    getCrudExtraTopHeight() {
      return 0;
    },
    currentSettlementMonth() {
      const now = new Date();
      return `${now.getFullYear()}-${this.padMonth(now.getMonth() + 1)}`;
    },
    splitSettlementMonth(value) {
      if (!value) return {};
      const parts = String(value).split('-');
      if (parts.length < 2) return {};
      const year = Number(parts[0]);
      const month = Number(parts[1]);
      if (!year || !month) return {};
      return {
        settlementYear: year,
        settlementMonth: month,
      };
    },
    settlementMonthLabel(row) {
      if (!row || !row.settlementYear || !row.settlementMonth) return '-';
      return `${row.settlementYear}-${this.padMonth(row.settlementMonth)}`;
    },
    padMonth(v) {
      return String(v || '').padStart(2, '0');
    },
    money(v, scale = 2) {
      const n = Number(v || 0);
      return n.toLocaleString('zh-CN', { minimumFractionDigits: scale, maximumFractionDigits: scale });
    },
    statusLabel(v) {
      const hit = STATUS_OPTIONS.find((item) => item.value === v);
      return hit ? hit.label : (v || '-');
    },
    statusColor(v) {
      const map = { WAIT_FILL: '#d46b08', WAIT_CONFIRM: '#1f7aec', DONE: '#009a29', CANCELLED: '#86909c' };
      return map[v] || '#4e5969';
    },
    operationLabel(v) {
      const map = { GENERATE: '生成', ADJUST: '业务量调整', MANUAL_FILL: '手工填报', SUBMIT: '提交确认', CANCEL: '作废' };
      return map[v] || v;
    },
    canEdit(row) {
      return row && !['DONE', 'CANCELLED'].includes(row.billStatus);
    },
    isWeightItem(row) {
      return row && row.measureUnit && row.measureUnit.indexOf('吨') > -1;
    },
    isVehicleItem(row) {
      return row && row.measureUnit && row.measureUnit.indexOf('车') > -1;
    },
    isCountItem(row) {
      return row && row.measureUnit && (row.measureUnit.indexOf('车') > -1 || row.measureUnit.indexOf('箱') > -1);
    },
    canAdjustQuantity(row) {
      return this.isWeightItem(row) || this.isCountItem(row);
    },
    quantityByUnit(row, type) {
      if (this.isWeightItem(row)) {
        return this.displayQuantity(row[type === 'adjust' ? 'adjustWeight' : 'systemWeight']);
      }
      if (this.isCountItem(row)) {
        return this.displayQuantity(row[type === 'adjust' ? 'adjustVehicleCount' : 'systemVehicleCount']);
      }
      return '-';
    },
    displayQuantity(value) {
      return value === null || value === undefined || value === '' ? '-' : value;
    },
    adjustmentQuantity(row) {
      if (this.isWeightItem(row)) return row.adjustWeight;
      if (this.isCountItem(row)) return row.adjustVehicleCount;
      return null;
    },
    withSettlementMonthParams(form) {
      const params = { ...form, ...this.splitSettlementMonth(form.settlementMonthValue) };
      delete params.settlementMonthValue;
      return params;
    },
    formatParams(params) {
      const result = crudMixins.methods.formatParams.call(this, params);
      if (result.settlementMonthValue) {
        Object.assign(result, this.splitSettlementMonth(result.settlementMonthValue));
        delete result.settlementMonthValue;
      }
      return result;
    },
    openDetail(row, tab = 'items') {
      getDetail(row.id).then((res) => {
        this.detail = res.data.data || res.data;
        this.activeTab = tab;
        this.detailVisible = true;
      });
    },
    resetDetail() {
      this.detail = null;
      this.activeTab = 'items';
    },
    openGenerateDialog() {
      this.generateForm = { settlementMonthValue: this.currentSettlementMonth() };
      this.generateVisible = true;
    },
    openBatchGenerateDialog() {
      this.batchGenerateForm = {
        settlementMonthValue: this.currentSettlementMonth(),
        generateReason: '批量生成',
      };
      this.batchGenerateVisible = true;
    },
    handleGenerate() {
      this.$refs.generateForm.validate(async (valid) => {
        if (!valid) return;
        this.generateLoading = true;
        try {
          await generateBill(this.withSettlementMonthParams(this.generateForm));
          this.$message.success('补生成成功');
          this.generateVisible = false;
          this.refreshChange();
        } finally {
          this.generateLoading = false;
        }
      });
    },
    handleBatchGenerate() {
      this.$refs.batchGenerateForm.validate((valid) => {
        if (!valid) return;
        const { settlementYear: year, settlementMonth: month } = this.splitSettlementMonth(this.batchGenerateForm.settlementMonthValue);
        this.$confirm(`将生成 ${year}年${month}月 所有未生成合同的结算单，已存在的合同将自动跳过，是否继续？`, '批量生成确认')
          .then(async () => {
            this.batchGenerateLoading = true;
            try {
              const res = await batchGenerateBill(this.withSettlementMonthParams(this.batchGenerateForm));
              const data = res.data.data || res.data || {};
              this.$alert(this.batchGenerateResultText(data), '批量生成结果', { dangerouslyUseHTMLString: true });
              this.batchGenerateVisible = false;
              this.refreshChange();
            } finally {
              this.batchGenerateLoading = false;
            }
          })
          .catch(() => {});
      });
    },
    batchGenerateResultText(data) {
      const failed = data.failedContracts || [];
      const skipped = data.skippedContracts || [];
      const lines = [
        `扫描合同：${data.totalContractCount || 0} 个`,
        `成功生成：${data.generatedCount || 0} 个`,
        `自动跳过：${data.skippedCount || 0} 个`,
        `生成失败：${data.failedCount || 0} 个`,
      ];
      if (failed.length) {
        lines.push('失败明细：');
        failed.slice(0, 5).forEach((item) => {
          lines.push(`${item.contractNo || item.contractId || '-'}：${item.reason || '-'}`);
        });
      } else if (skipped.length) {
        lines.push('跳过示例：');
        skipped.slice(0, 3).forEach((item) => {
          lines.push(`${item.contractNo || item.contractId || '-'}：${item.reason || '-'}`);
        });
      }
      return lines.join('<br>');
    },
    openAdjustDialog(row) {
      this.currentItem = row;
      this.adjustForm = {
        itemId: row.id,
        adjustQuantity: this.adjustmentQuantity(row),
        adjustReason: row.adjustReason,
      };
      this.adjustVisible = true;
    },
    handleAdjust() {
      this.$refs.adjustForm.validate(async (valid) => {
        if (!valid) return;
        this.itemSaving = true;
        try {
          const payload = {
            itemId: this.adjustForm.itemId,
            adjustReason: this.adjustForm.adjustReason,
          };
          if (this.isWeightItem(this.currentItem)) {
            payload.adjustWeight = this.adjustForm.adjustQuantity;
          }
          if (this.isCountItem(this.currentItem)) {
            payload.adjustVehicleCount = this.adjustForm.adjustQuantity;
          }
          await adjustItem(payload);
          this.$message.success('调整保存成功');
          this.adjustVisible = false;
          await this.reloadDetail();
          this.refreshChange();
        } finally {
          this.itemSaving = false;
        }
      });
    },
    openManualDialog(row) {
      this.currentItem = row;
      this.manualForm = {
        itemId: row.id,
        finalQuantity: row.finalQuantity,
        unitPriceWithoutTax: row.unitPriceWithoutTax,
        fillRemark: row.fillRemark,
      };
      this.manualVisible = true;
    },
    handleManualFill() {
      this.$refs.manualForm.validate(async (valid) => {
        if (!valid) return;
        this.itemSaving = true;
        try {
          await manualFillItem({
            itemId: this.manualForm.itemId,
            finalQuantity: this.manualForm.finalQuantity,
            unitPriceWithoutTax: this.manualForm.unitPriceWithoutTax,
            fillRemark: this.manualForm.fillRemark,
          });
          this.$message.success('填报保存成功');
          this.manualVisible = false;
          await this.reloadDetail();
          this.refreshChange();
        } finally {
          this.itemSaving = false;
        }
      });
    },
    async reloadDetail() {
      if (!this.detail || !this.detail.id) return;
      const res = await getDetail(this.detail.id);
      this.detail = res.data.data || res.data;
    },
    handleSubmit(row) {
      this.$prompt('请输入提交说明', '提交确认', { inputType: 'textarea' }).then(({ value }) => {
        return submitBill({ id: row.id, submitRemark: value }).then(() => {
          this.$message.success('提交确认成功');
          this.refreshChange();
          if (this.detail && this.detail.id === row.id) this.reloadDetail();
        });
      }).catch(() => {});
    },
    handleBatchSubmit() {
      const rows = (this.selectionList || []).filter((row) => this.canEdit(row));
      if (!rows.length) {
        this.$message.warning('请选择可提交的结算单');
        return;
      }
      this.$confirm(`确定提交 ${rows.length} 张结算单？`).then(async () => {
        this.batchSubmitLoading = true;
        try {
          for (const row of rows) {
            await submitBill({ id: row.id, submitRemark: '批量提交确认' });
          }
          this.$message.success('批量提交成功');
          this.refreshChange();
        } finally {
          this.batchSubmitLoading = false;
        }
      }).catch(() => {});
    },
    handleCancel(row) {
      this.$prompt('请输入作废原因', '作废结算单', { inputType: 'textarea' }).then(({ value }) => {
        return cancelBill({ id: row.id, submitRemark: value }).then(() => {
          this.$message.success('作废成功');
          this.refreshChange();
          if (this.detail && this.detail.id === row.id) this.reloadDetail();
        });
      }).catch(() => {});
    },
    async handleExport() {
      if ((this.page.total || 0) < 1) {
        this.$message.warning('无数据可导出');
        return;
      }
      if (this.page.total > 10000) {
        this.$message.warning('当前最多导出 10000 条，请缩小筛选范围后重试');
        return;
      }
      this.exportLoading = true;
      try {
        const params = this.formatParams(Object.assign({}, this.query));
        const res = await exportExcel(params);
        downloadXls(res.data, `合同结算单_${Date.now()}.xlsx`);
        this.$message.success('导出成功');
      } finally {
        this.exportLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.sub-line {
  margin-top: 2px;
  font-size: 12px;
  color: #86909c;
  line-height: 1.4;
}
.money {
  display: inline-block;
  width: 100%;
  font-family: Consolas, Monaco, monospace;
  text-align: right;
}
.strong {
  font-weight: 600;
}
.danger-link {
  color: #d03050;
}
.detail-head {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  padding: 0 20px 12px;
}
.detail-card {
  min-height: 56px;
  padding: 8px 10px;
  border: 1px solid #edf1f6;
  border-radius: 4px;
  background: #fbfcfe;
}
.detail-label {
  font-size: 12px;
  color: #86909c;
}
.detail-value {
  margin-top: 6px;
  font-weight: 600;
  color: #1d2129;
  word-break: break-all;
}
.summary {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  padding: 0 20px 12px;
}
.summary-item {
  padding: 10px;
  border-radius: 4px;
  background: #f7f8fa;
  text-align: right;
  span {
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
    color: #86909c;
  }
  b {
    font-family: Consolas, Monaco, monospace;
    font-size: 16px;
  }
}
.log-operator {
  margin-left: 8px;
}
::v-deep .el-drawer__body {
  padding: 0 20px 20px;
  overflow: auto;
}
::v-deep .el-tabs__content {
  overflow: visible;
}
</style>
